import moment from "moment";
/**
 * Formatea las fechas 'desde' y 'hasta' en el elemento dado si no están en el formato 'YYYY-MM-DD'.
 * Establece el campo 'id' en null.
 *
 * @param {Object} item - El objeto que contiene las fechas a formatear.
 * @param {string} item.desde - La cadena de fecha 'desde'.
 * @param {string} item.hasta - La cadena de fecha 'hasta'.
 * @returns {Object} Un nuevo objeto con las fechas 'desde' y 'hasta' formateadas y 'id' establecido en null.
 */
const formatDatesIfNeeded = (item, needIdNull) => {
  const isDateValidFormat = (dateString) =>
    /^\d{4}-\d{2}-\d{2}$/.test(dateString);

  const formatDate = (dateString) =>
    isDateValidFormat(dateString)
      ? dateString
      : moment(dateString, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD");

  return {
    ...item,
    id: needIdNull ? null : item.id,
    desde: formatDate(item.desde),
    hasta: formatDate(item.hasta),
  };
};

/**
 * Calcula la tarifa mensual, diaria y en pesos chilenos (CLP) basada en el metraje y la tarifa del cliente.
 *
 * @param {Object} item - El objeto que contiene los datos necesarios para calcular la tarifa.
 * @param {string} item.descripcion - La descripción del ítem.
 * @param {string} item.desde - La fecha de inicio.
 * @param {string} item.hasta - La fecha de fin.
 * @param {number} item.tarifa_cliente - La tarifa del cliente.
 * @param {string} item.moneda - La moneda en la que se expresa la tarifa del cliente.
 * @param {number} item.cantidad - La cantidad a ser calculada.
 * @param {string} item.cobro - El tipo de cobro ('exento' o 'afecto').
 * @param {Object[]} monedaOptions - Lista de opciones de moneda con sus tasas de cambio.
 */
function calculateTarifaMensualWithMetraje(item, monedaOptions) {
  if (
    item.descripcion !== "Gasto Común" &&
    item.descripcion !== "Electricidad" &&
    item.descripcion !== "Agua"
  ) {
    if (item.desde && item.hasta) {
      if (item.tarifa_cliente > 0) {
        let tarifaDiaria = 0;
        let tarifaMensual = 0;

        if (item.moneda !== "UF") {
          let ufExchangeRateItem;
          let monedaExchangeRateItem;
          if (monedaOptions.length > 0) {
            ufExchangeRateItem = monedaOptions.find(
              (option) => option.codigo === "UF"
            );
            monedaExchangeRateItem = monedaOptions.find(
              (option) => option.codigo === item.moneda
            );

            if (ufExchangeRateItem && monedaExchangeRateItem) {
              const ufExchangeRate = ufExchangeRateItem.tipoCambio;
              const monedaExchangeRate = monedaExchangeRateItem.tipoCambio;

              // Calculate equivalent in UF
              const equivalentInUF =
                (item.tarifa_cliente * monedaExchangeRate) / ufExchangeRate;

              // Calculate daily tariff and truncate it
              tarifaDiaria = (equivalentInUF * item.cantidad) / 30;

              // Multiply by 30 to get monthly tariff and then truncate it
              tarifaMensual = tarifaDiaria * 30;
            } else {
              console.error(
                "Exchange rate for 'UF' or item currency not found."
              );
              return;
            }
          }
        } else {
          // If the currency is "UF", calculate daily tariff directly and truncate
          tarifaDiaria = (item.tarifa_cliente * item.cantidad) / 30;

          // Multiply by 30 to get monthly tariff and truncate
          tarifaMensual = tarifaDiaria * 30;
        }

        // Assign truncated values to the item
        item.tarifa_diaria = tarifaDiaria;
        item.tarifa_mensual = tarifaMensual;
      } else {
        item.tarifa_mensual = 0;
        item.tarifa_diaria = 0;
      }
    } else {
      item.tarifa_mensual = 0;
      item.tarifa_diaria = 0;
    }
  }
}

/**
 * Calcula la tarifa mensual y diaria de un servicio, ajustada según el tipo de moneda y otras condiciones.
 *
 * @param {Object} item - El objeto que contiene los datos necesarios para calcular la tarifa.
 * @param {string} item.descripcion - La descripción del ítem.
 * @param {string} item.desde - La fecha de inicio.
 * @param {string} item.hasta - La fecha de fin.
 * @param {number} item.tarifa_cliente - La tarifa del cliente.
 * @param {string} item.moneda - La moneda en la que se expresa la tarifa del cliente.
 * @param {number} item.cantidad - La cantidad a ser calculada.
 * @param {string} item.tramo - El tramo de gasto común, si aplica.
 * @param {string} item.cobro - El tipo de cobro ('Factura Exenta' o 'Factura Afecta').
 * @param {Object[]} monedaOptions - Lista de opciones de moneda con sus tasas de cambio.
 * @param {Object[]} flattenedDataArriendo - Lista de datos de arriendo, utilizada para encontrar ítems coincidentes en gastos comunes.
 * @param {number} subtotalArriendo - El subtotal del arriendo, utilizado para calcular gastos comunes.
 */
function calculateTarifaMensualServicios(
  item,
  monedaOptions,
  flattenedDataArriendo,
  subtotalArriendo
) {
  // Gasto Comun en UF con descuento del 100%: la tarifa queda en 0 y no se
  // recalcula desde la tarifa base.
  const descuentoTotalUf =
    item.moneda === "UF" && parseFloat(item.descuento) >= 100;

  function calculateGastoComunTariff() {
    const matchingItems = flattenedDataArriendo.filter(
      (data) =>
        (data.tramo === item.tramo &&
          item.descripcion.split(" - ")[1] === data.descripcion) ||
        ((data.tramo === null || data.tramo === "null" || data.tramo === "") &&
          item.descripcion.split(" - ")[1] ===
            data.descripcion.split(" - ")[1] &&
          item.relacion === "producto")
    );
    const totalTarifaMensual = matchingItems.reduce(
      (sum, matchingItem) => sum + parseFloat(matchingItem.tarifa_mensual || 0),
      0
    );

    item.tarifa_base = totalTarifaMensual * 0.1;
    let tarifaDiaria = 0;
    let tarifaMensual = 0;

    if (item.moneda !== "UF") {
      let ufExchangeRateItem;
      let monedaExchangeRateItem;
      if (monedaOptions.length > 0) {
        ufExchangeRateItem = monedaOptions?.find(
          (option) => option?.codigo === "UF"
        );
        monedaExchangeRateItem = monedaOptions?.find(
          (option) => option?.codigo === item.moneda
        );

        if (ufExchangeRateItem && monedaExchangeRateItem) {
          const ufExchangeRate = ufExchangeRateItem.tipoCambio;
          const monedaExangeRate = monedaExchangeRateItem.tipoCambio;

          // Calculate equivalent in UF
          const equivalentInUF =
            (item.tarifa_cliente * monedaExangeRate) / ufExchangeRate;

          // Calculate daily tariff first without rounding
          tarifaDiaria = (equivalentInUF ) / 30;

          // Calculate monthly tariff from daily tariff
          tarifaMensual = tarifaDiaria * 30;
        } else {
          console.error("Exchange rate for 'UF' or item currency not found.");
          return;
        }
      }
    } else {
      // If the currency is "UF", calculate daily tariff directly
      tarifaDiaria =
        item.tarifa_cliente
          ? item.tarifa_cliente / 30
          : descuentoTotalUf
          ? 0
          : item.tarifa_base / 30;
      tarifaMensual = tarifaDiaria * 30;
    }

    // Round both tarifa_mensual and tarifa_diaria at the end
    item.tarifa_diaria = parseFloat(tarifaDiaria);
    item.tarifa_mensual = parseFloat(tarifaMensual);
  }

  if (item.descripcion !== "Electricidad" && item.descripcion !== "Agua") {
    if (item.desde && item.hasta) {
      if (item.tarifa_cliente > 0) {
        let tarifaDiaria = 0;
        let tarifaMensual = 0;

        if (item.moneda !== "UF") {
          let ufExchangeRateItem;
          let monedaExchangeRateItem;
          if (monedaOptions.length > 0) {
            ufExchangeRateItem = monedaOptions?.find(
              (option) => option?.codigo === "UF"
            );
            monedaExchangeRateItem = monedaOptions?.find(
              (option) => option?.codigo === item.moneda
            );

            if (ufExchangeRateItem && monedaExchangeRateItem) {
              const ufExchangeRate = ufExchangeRateItem.tipoCambio;
              const monedaExangeRate = monedaExchangeRateItem.tipoCambio;

              // Calculate equivalent in UF
              const equivalentInUF =
                (item.tarifa_cliente * monedaExangeRate) / ufExchangeRate;

              // Calculate daily tariff first without rounding
              tarifaDiaria = (equivalentInUF ) / 30;

              // Calculate monthly tariff from daily tariff
              tarifaMensual = tarifaDiaria * 30;
            } else {
              console.error(
                "Exchange rate for 'UF' or item currency not found."
              );
              return;
            }
          }
        } else {
          // If the currency is "UF", calculate daily tariff directly
          tarifaDiaria = item.tarifa_cliente / 30;
          tarifaMensual = tarifaDiaria * 30;
        }

        // Round both tarifa_mensual and tarifa_diaria at the end
        item.tarifa_diaria = parseFloat(tarifaDiaria);
        item.tarifa_mensual = parseFloat(tarifaMensual);
      } else {
        if (item.descripcion.includes("Gasto Comun") && descuentoTotalUf) {
          // Gasto Comun en UF con descuento del 100%: tarifa en 0
          item.tarifa_mensual = 0;
          item.tarifa_diaria = 0;
        } else if (item.descripcion.includes("Gasto Comun")) {
          calculateGastoComunTariff();
        } else {
          item.tarifa_mensual = 0;
          item.tarifa_diaria = 0;
        }
      }
    } else {
      item.tarifa_mensual = 0;
      item.tarifa_diaria = 0;
    }
  }

  if (
    item.descripcion.includes("Gasto Comun") &&
    !descuentoTotalUf
  ) {
    calculateGastoComunTariff();
  }
}
/**
 * Calcula la tarifa mensual y diaria de un servicio único, ajustada según el tipo de moneda y otras condiciones.
 *
 * @param {Object} item - El objeto que contiene los datos necesarios para calcular la tarifa.
 * @param {string} item.desde - La fecha de inicio.
 * @param {string} item.hasta - La fecha de fin.
 * @param {number} item.tarifa_cliente - La tarifa del cliente.
 * @param {string} item.moneda - La moneda en la que se expresa la tarifa del cliente.
 * @param {string} item.cobro - El tipo de cobro ('Factura Exenta' o 'Factura Afecta').
 * @param {Object[]} monedaOptions - Lista de opciones de moneda con sus tasas de cambio.
 */
function calculateTarifaMensualServiciosUnicos(item, monedaOptions) {
  if (item.desde && item.hasta) {
    if (item.tarifa_cliente > 0) {
      let tarifaDiaria = 0;
      let tarifaMensual = 0;

      if (item.moneda !== "UF") {
        let ufExchangeRateItem;
        let monedaExchangeRateItem;
        if (monedaOptions.length > 0) {
          ufExchangeRateItem = monedaOptions?.find(
            (option) => option?.codigo === "UF"
          );
          monedaExchangeRateItem = monedaOptions?.find(
            (option) => option?.codigo === item.moneda
          );

          if (ufExchangeRateItem && monedaExchangeRateItem) {
            const ufExchangeRate = ufExchangeRateItem.tipoCambio;
            const monedaExangeRate = monedaExchangeRateItem.tipoCambio;

            // Calculate equivalent in UF without rounding yet
            const equivalentInUF =
              (item.tarifa_cliente * monedaExangeRate) / ufExchangeRate;

            // Calculate daily tariff first without rounding
            tarifaDiaria = equivalentInUF / 30;

            // Calculate monthly tariff from daily tariff
            tarifaMensual = tarifaDiaria * 30;
          } else {
            console.error("Exchange rate for 'UF' or item currency not found.");
            return;
          }
        }
      } else {
        // If the currency is "UF", calculate daily tariff directly
        tarifaDiaria = item.tarifa_cliente / 30;
        tarifaMensual = tarifaDiaria * 30;
      }

      // Round both tarifa_mensual and tarifa_diaria at the end
      item.tarifa_diaria = parseFloat(tarifaDiaria);
      item.tarifa_mensual = parseFloat(tarifaMensual);
    } else {
      item.tarifa_mensual = 0;
      item.tarifa_diaria = 0;
    }
  } else {
    item.tarifa_mensual = 0;
    item.tarifa_diaria = 0;
  }
}

/**
 * Calcula el subtotal mensual de una lista de datos, ajustado por la cantidad de meses de cada ítem.
 *
 * @param {Object[]} data - Lista de objetos que contienen los datos necesarios para calcular el subtotal.
 * @param {string} data[].descripcion - La descripción del ítem.
 * @param {string} data[].desde - La fecha de inicio.
 * @param {string} data[].hasta - La fecha de fin.
 * @param {number|string} data[].tarifa_mensual - La tarifa mensual del ítem. Si es "Tarifa Variable", se ignora.
 *
 * @returns {string} - El subtotal mensual calculado, redondeado a una decimal.
 */
const calculateSubtotal = (data) => {
  let subtotal = 0;
  let descripcionSum = {};
  let descripcionMonths = {};

  for (let i = 0; i < data.length; i++) {
    const rowData = data[i];

    if (
      rowData &&
      rowData.tarifa_mensual !== undefined &&
      rowData.tarifa_mensual !== "Tarifa Variable"
    ) {
      const currentNombre = rowData.descripcion;
      const currentTarifaMensual = parseFloat(rowData.tarifa_mensual).toFixed(
        2
      );

      const desdeDate = new Date(rowData.desde);
      const hastaDate = new Date(rowData.hasta);

      // Get the time zone offset of desdeDate and hastaDate
      const desdeOffset = desdeDate.getTimezoneOffset() * 60 * 1000; // Convert minutes to milliseconds
      const hastaOffset = hastaDate.getTimezoneOffset() * 60 * 1000; // Convert minutes to milliseconds

      // Adjust dates with respective time zone offsets
      const desde = new Date(desdeDate.getTime() + desdeOffset);
      desde.setHours(0, 0, 0, 0);

      const hasta = new Date(hastaDate.getTime() + hastaOffset);
      hasta.setHours(0, 0, 0, 0);

      const monthsDifference =
        (hasta.getFullYear() - desde.getFullYear()) * 12 +
        (hasta.getMonth() - desde.getMonth());

      // If monthsDifference is 0, set it to 1
      const adjustedMonthsDifference =
        monthsDifference === 0 ? 1 : monthsDifference;

      // Add current tarifa to the sum and increment total months for this descripcion
      if (currentNombre in descripcionSum) {
        descripcionSum[currentNombre] = parseFloat(
          (
            parseFloat(descripcionSum[currentNombre]) +
            parseFloat(currentTarifaMensual) * adjustedMonthsDifference
          ).toFixed(2)
        );
        descripcionMonths[currentNombre] += adjustedMonthsDifference;
      } else {
        descripcionSum[currentNombre] = parseFloat(
          (parseFloat(currentTarifaMensual) * adjustedMonthsDifference).toFixed(
            2
          )
        );
        descripcionMonths[currentNombre] = adjustedMonthsDifference;
      }
    }
  }

  for (const nombre in descripcionSum) {
    subtotal = parseFloat(
      (
        parseFloat(subtotal) +
        descripcionSum[nombre] / descripcionMonths[nombre]
      ).toFixed(2)
    );
  }

  return subtotal.toFixed(2);
};

const validateFiltrosProductosCabecera = ({
  empresa,
  tipoProducto,
  desde,
  hasta,
  regularizarCotizacion,
}) => {
  const basicChecks =
    empresa !== null &&
    empresa !== undefined &&
    empresa !== "" &&
    tipoProducto !== null &&
    tipoProducto !== undefined &&
    tipoProducto !== "" &&
    desde !== null &&
    desde !== undefined &&
    hasta !== null &&
    hasta !== undefined &&
    hasta !== "";

  const desdeDate = new Date(desde);
  const hastaDate = new Date(hasta);

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  desdeDate.setHours(0, 0, 0, 0);
  hastaDate.setHours(0, 0, 0, 0);

  const dateChecks = hastaDate > desdeDate;
  const currentDateChecks = desdeDate >= currentDate && hastaDate >= currentDate;

  const isDateValid = regularizarCotizacion
    ? dateChecks
    : dateChecks && currentDateChecks;
  const isValid = basicChecks && isDateValid;

  if (!basicChecks) {
    return {
      isValid: false,
      errorMessage: "Debe ingresar todos los parámetros de búsqueda",
    };
  }

  if (!isDateValid) {
    return {
      isValid: false,
      errorMessage: regularizarCotizacion
        ? "La fecha de término debe ser mayor a la fecha de inicio"
        : "Las fechas deben ser mayores o iguales a hoy y la fecha de término debe ser mayor a la de inicio",
    };
  }

  return { isValid, errorMessage: null };
};

function sortDataByProductAndSubProduct(data) {
  if (!Array.isArray(data)) {
    console.error("Expected an array but received:", data);
    return data; // Return an empty array to prevent errors
  }
  return data.sort((a, b) => {
    const mainProductA =
      a.relacion === "producto"
        ? a.descripcion
        : a.descripcion.split(" - ")[1] || a.descripcion;
    const mainProductB =
      b.relacion === "producto"
        ? b.descripcion
        : b.descripcion.split(" - ")[1] || b.descripcion;

    if (mainProductA < mainProductB) return -1;
    if (mainProductA > mainProductB) return 1;

    if (a.relacion === "producto" && b.relacion !== "producto") return -1;
    if (a.relacion !== "producto" && b.relacion === "producto") return 1;

    return a.descripcion.localeCompare(b.descripcion);
  });
}

export {
  sortDataByProductAndSubProduct,
  calculateSubtotal,
  calculateTarifaMensualWithMetraje,
  calculateTarifaMensualServicios,
  calculateTarifaMensualServiciosUnicos,
  formatDatesIfNeeded,
  validateFiltrosProductosCabecera,
};
